<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'accounts',
    path: '/accounts',
    component: 'AccountsView',
    title: () => pgettext('AccountsView', 'Cash and Bank Accounts'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'AccountsView',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    companyId: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'name',
      name: $fn.value.pgettext('AccountsView', 'Name'),
      type: 'command',
      formatter: {
        name: 'link',
        href: '#',
      },
    },
    {
      field: 'account_type',
      name: $fn.value.pgettext('AccountsView', 'Type'),
      formatter: {
        name: 'gettext',
        context: 'AccountTypes',
        conversion: 'Sentence',
        filterInput: true,
      },
    },
    {
      field: 'currency',
      name: $fn.value.pgettext('AccountsView', 'Currency'),
    },
    {
      field: 'description',
      name: $fn.value.pgettext('AccountsView', 'Description'),
    },
    {
      field: 'account_number',
      name: $fn.value.pgettext('AccountsView', 'Account nr'),
    },
    {
      field: 'bank',
      name: $fn.value.pgettext('AccountsView', 'Bank'),
    },
    {
      field: 'iban',
      name: $fn.value.pgettext('AccountsView', 'IBAN'),
    },
    {
      field: 'swift',
      name: $fn.value.pgettext('AccountsView', 'SWIFT'),
    },
    {
      field: 'branch',
      name: $fn.value.pgettext('AccountsView', 'Branch'),
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('list_account', {
      parameters: { owner_id: props.companyId },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('AccountsView', 'Cash and Bank Accounts'),
      icon: $config.value.account.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.companyId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('companyId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_sqbeoi6yn_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_z8xh2i5es_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_nen0csh3q_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Account',
      props: {
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_ee2xpyhep_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'name';
  };
  const act_4615685607825208_showModal_3ore4hi06_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Account',
      props: {
        ownerId: props.companyId,
      },
    };
  };

  const act_7315092382398562_closeModal_sqbeoi6yn =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_sqbeoi6yn_getActionArgs,
    });
  const inst_evh_7315092382398562_close_qymt5moes =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_sqbeoi6yn,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_qymt5moes],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_gm02apq54 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_bjibdy640 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_gm02apq54,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_bjibdy640],
    event: 'reload',
  });
  const act_2248226175642056_setData_z8xh2i5es = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_z8xh2i5es_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_qlelsbjd2 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_z8xh2i5es,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_qlelsbjd2],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_nen0csh3q =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_nen0csh3q_getActionArgs,
    });
  const inst_evh_2248226175642056_command_ee2xpyhep =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_nen0csh3q,
      event: 'command',
      when: inst_evh_2248226175642056_command_ee2xpyhep_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_command_ee2xpyhep],
    event: 'command',
  });
  const act_4615685607825208_showModal_3ore4hi06 =
    new actionTypes.ShowModalAction({
      actionArgs: act_4615685607825208_showModal_3ore4hi06_getActionArgs,
    });
  const inst_evh_4615685607825208_click_xj5byipjb =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_showModal_3ore4hi06,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_4615685607825208_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4615685607825208_click_xj5byipjb],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_qymt5moes,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_bjibdy640,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_qlelsbjd2,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_ee2xpyhep,
    inst_evh_2248226175642056_command,
    inst_evh_4615685607825208_click_xj5byipjb,
    inst_evh_4615685607825208_click,
  };

  const evh_7315092382398562_close_qymt5moes = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_qymt5moes.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_bjibdy640 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_bjibdy640.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_qlelsbjd2 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_qlelsbjd2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_ee2xpyhep = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_ee2xpyhep.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click_xj5byipjb = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click_xj5byipjb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="AccountsView"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="accounts"
      :headerTitle="pgettext('AccountsView', 'Cash and Bank Accounts')"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'accounts')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <ItpButton
            :icon="$config.account.createAccountIcon"
            :text="pgettext('AccountsView', 'Add Account')"
            size="sm"
            variant="light"
            @click="evh_4615685607825208_click($event, {})"
          >
          </ItpButton>
          <PageHelp path="/finance/accounts.html"> </PageHelp>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
